import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  CollectionRecord,
  HuntPackageRecord,
  ThreatProfile,
} from '@cyborg-monorepo/hunter/common';
import { forkJoin, map, Observable, of } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { HuntPackageService } from '../../shared/services/hunt-package.service';
import { ThreatProfileService } from '../../shared/services/threat-profile.service';

export interface CollectionData {
  collection: CollectionRecord;
  huntPackages: Array<HuntPackageRecord>;
  threatProfiles: Array<ThreatProfile>;
}

@Injectable({
  providedIn: 'root',
})
export class CollectionService {
  constructor(
    private httpClient: HttpClient,
    private huntPackageService: HuntPackageService,
    private threatProfileService: ThreatProfileService
  ) {}

  public getCollectionByUUID = (uuid: string): Observable<CollectionRecord> => {
    return this.httpClient.get<CollectionRecord>(
      `${environment.api}/es/cyborg-collection/${uuid}`
    );
  };

  public getRelatedCollectionsByHuntPackageUUID = (
    uuid: string
  ): Observable<Array<CollectionRecord>> => {
    return this.httpClient.get<Array<CollectionRecord>>(
      `${environment.api}/es/cyborg-collection/related/${uuid}`
    );
  };

  public getCollectionsByUUID(
    uuids: Array<string>
  ): Observable<Array<CollectionRecord>> {
    const searchParams: { [param: string]: string | string[] } = { uuids };
    return this.httpClient.get<Array<CollectionRecord>>(
      `${environment.api}/es/cyborg-collection/list/`,
      {
        params: searchParams,
      }
    );
  }

  public getEmergingThreatCollections(): Observable<Array<CollectionRecord>> {
    return this.httpClient.get<Array<CollectionRecord>>(
      `${environment.api}/es/cyborg-collection/emergingThreats`
    );
  }

  /**
   * Helper method to take a collection record returned from the API and resolve the
   * related links to their full payloads
   */
  public resolveCollectionLinks = (
    collection: CollectionRecord
  ): Observable<CollectionData> => {
    return forkJoin({
      huntPackages: this.huntPackageService
        .getHuntPackagesbyUUIDs(collection.hunter_packages)
        .pipe(
          map((huntPackages) => {
            return huntPackages.sort(
              (ucA, ucB) =>
                new Date(ucB.last_updated).valueOf() -
                new Date(ucA.last_updated).valueOf()
            );
          })
        ),
      collection: of(collection),
      threatProfiles: this.threatProfileService
        .getThreatProfilesbyUUIDs(collection.threat_profiles)
        .pipe(
          map((threatProfiles) => {
            return threatProfiles.sort((ucA, ucB) =>
              ucA.title.localeCompare(ucB.title)
            );
          })
        ),
    });
  };
}
